import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { FiAlertTriangle } from 'react-icons/fi';
import { CartProvider } from './contexts/CartContext';
import { ShopProvider } from './contexts/ShopContext';
import { OwnerProvider } from './contexts/OwnerContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CommunityProvider } from './contexts/CommunityContext';
import ShopLayout from './components/ShopLayout';
import ErrorBoundary from './components/ErrorBoundary';
import SoundEffects from './components/SoundEffects';
import CommunityLayout from './components/community/CommunityLayout';
import OAuthHandler from './components/OAuthHandler';
import RouteProgress from './components/RouteProgress';
import CommunityFeed from './pages/community/CommunityFeed';
import PostDetailPage from './pages/community/PostDetailPage';
import CommunityUserPage from './pages/community/CommunityUserPage';
import CommunityMePage from './pages/community/CommunityMePage';
import ChannelsPage from './pages/community/ChannelsPage';
import ChannelPage from './pages/community/ChannelPage';
import GroupsPage from './pages/community/GroupsPage';
import GroupPage from './pages/community/GroupPage';
import JoinGroupPage from './pages/community/JoinGroupPage';
import VerifyPage from './pages/community/VerifyPage';
import BlockedPage from './pages/community/BlockedPage';
import PeoplePage from './pages/community/PeoplePage';
import NotificationsPage from './pages/community/NotificationsPage';
import MessagesPage from './pages/community/MessagesPage';
import ShopsPage from './pages/community/ShopsPage';
import ReelsPage from './pages/community/ReelsPage';
import LeaderboardPage from './pages/community/LeaderboardPage';
import ShopPage from './pages/community/ShopPage';
import ShopProductPage from './pages/community/ShopProductPage';
import HomePage from './pages/HomePage';
import CreateShop from './pages/CreateShop';
import ShopHome from './pages/ShopHome';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import About from './pages/About';
import MyOrders from './pages/MyOrders';
import Profile from './pages/Profile';
import ArticlesPage from './pages/ArticlesPage';
import VideosPage from './pages/VideosPage';
import { useLanguage } from './i18n';

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <CartProvider>
          <OwnerProvider>
            <CommunityProvider>
              {/* Click / tap sounds for the whole storefront (mute in the navbar) */}
              <SoundEffects />
              {/* Top bar that animates on every route change (click / Back) */}
              <RouteProgress />
              {/* Restores a “login with Google” session coming back from the API */}
              <OAuthHandler />
              <Routes>
                {/* Mini Shop Community — the first thing a visitor sees. */}
                <Route element={<CommunityLayout />}>
                  <Route path="/" element={<CommunityFeed />} />
                  <Route path="/people" element={<PeoplePage />} />
                  <Route path="/shops" element={<ShopsPage />} />
                  <Route path="/reels" element={<ReelsPage />} />
                  <Route path="/leaderboard" element={<LeaderboardPage />} />
                  {/* Community shop: every product of every shop + product details */}
                  <Route path="/shop/product/:id" element={<ShopProductPage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/post/:id" element={<PostDetailPage />} />
                  <Route path="/u/:username" element={<CommunityUserPage />} />
                  <Route path="/me" element={<CommunityMePage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/messages" element={<MessagesPage />} />
                  <Route path="/messages/:userId" element={<MessagesPage />} />
                  {/* Channels (subscribe) · groups (invite links + chat) · the ✓ */}
                  <Route path="/channels" element={<ChannelsPage />} />
                  <Route path="/channel/:ref" element={<ChannelPage />} />
                  <Route path="/groups" element={<GroupsPage />} />
                  <Route path="/groups/:id" element={<GroupPage />} />
                  <Route path="/join/:code" element={<JoinGroupPage />} />
                  <Route path="/verify" element={<VerifyPage />} />
                  <Route path="/blocked" element={<BlockedPage />} />
                  {/* The old marketing landing page (about our service). */}
                  <Route path="/service" element={<HomePage />} />
                </Route>

                <Route path="/create-shop" element={<CreateShop />} />
                <Route
                  path="/:username"
                  element={
                    <ShopProvider>
                      <ShopLayout />
                    </ShopProvider>
                  }
                >
                  <Route index element={<ShopHome />} />
                  <Route path="products" element={<Products />} />
                  <Route path="product/:id" element={<ProductDetail />} />
                  <Route path="checkout" element={<Checkout />} />
                  <Route path="order-success" element={<OrderSuccess />} />
                  <Route path="my-orders" element={<MyOrders />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="about" element={<About />} />
                  <Route path="articles" element={<ArticlesPage />} />
                  <Route path="videos" element={<VideosPage />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </CommunityProvider>
          </OwnerProvider>
        </CartProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function NotFound() {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <FiAlertTriangle className="w-16 h-16 text-gray-300 mb-4" />
      <h1 className="text-4xl font-bold text-gray-800">404</h1>
      <p className="text-gray-500 mt-2">{t('notFound404')}</p>
      <a href="/" className="btn-primary mt-6 px-6 py-2 rounded-lg">{t('backHome')}</a>
    </div>
  );
}
